import { useSessionNotification } from '../../utils/actionableNotifications';
import React, { useEffect, useMemo, useRef } from "react";
import dayjs from "dayjs";
import { Avatar, Button } from 'antd';
import { CountBadge as Badge } from '../shared/CountBadge';
import { ShopOutlined, UserOutlined, WhatsAppOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useCanonicalMessaging } from "../contexts/CanonicalMessagingContext";
import incomingSound from "../../assets/sounds/wati_incoming_sound.mp3";
import { playNotificationSound } from '../../utils/notificationSound';
import { personalSettingsUserId } from '../../utils/personalSettings';
import chatBackground from "../../assets/bg-chat.png";
import { MEDIA_MESSAGE_LABELS } from "./mediaMessageLabels";
import { loadChatNotificationPreferences, useChatNotificationPreferences } from "./chatNotificationPreferences";
import AppTheme from "../theme/AppTheme";
import { useAppearance } from "../contexts/AppearanceContext";
import { ct } from "../qv4/MessageParts";
import { formatWaTextInlineHtml } from "../qv4/whatsappTextFormatting";
import { customerTypeLook } from '../qv4/customerTypeLook';
import { withNotificationActions } from '../../utils/actionableNotifications';
import { canonicalOnlyChat, chatDisplayName } from './canonicalVisibleAdapter';
import { firstNonBlankText } from './contactDisplayName';

function ChatNotificationTitle({ conversationId, senderName }) {
  const messaging = useCanonicalMessaging();
  const conversation = messaging.conversations[conversationId];
  const { isDark } = useAppearance();
  const audience = messaging.channelAccounts.find(account => account.id === conversation?.channel_account_id)?.audience || 'customer';
  const colors = ct(isDark, audience);
  const count = Number(conversation?.viewer_state?.unread_count);
  const unread = Number.isSafeInteger(count) && count > 0 ? count : 0;
  const namedConversation = firstNonBlankText(conversation?.business_context?.customer_name, conversation?.participant?.display_name);
  const name = namedConversation ? chatDisplayName(canonicalOnlyChat(conversation))
    : firstNonBlankText(senderName, conversation && chatDisplayName(canonicalOnlyChat(conversation))) || 'New message';
  return <AppTheme><div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
    {unread > 0 && <span aria-label={`${unread} unread messages`} style={{ flexShrink: 0 }}><Badge count={unread} overflowCount={Number.MAX_SAFE_INTEGER} style={{ backgroundColor: colors.accent, color: colors.onAccent }} /></span>}
  </div></AppTheme>;
}

function CustomerNotificationAvatar({ conversationId }) {
  const messaging = useCanonicalMessaging();
  const conversation = messaging.conversations[conversationId];
  const look = customerTypeLook(conversation?.business_context?.customer_type);
  const { isDark } = useAppearance(), colors = ct(isDark, 'customer');
  const name = conversation ? chatDisplayName(canonicalOnlyChat(conversation)) : '';
  const hasName = /\p{L}/u.test(name);
  const initials = name.trim().split(/\s+/).slice(0, 2).map(part => Array.from(part)[0]).join('').toUpperCase();
  return <Avatar aria-label={`${look.label || 'Unset type'} customer message`} size={26} icon={look.label ? look.icon : hasName ? undefined : <UserOutlined />} style={{ backgroundColor: look.label ? look.color : colors.chrome, color: look.label ? undefined : colors.accentText }}>
    {!look.label && hasName ? initials : null}
  </Avatar>;
}

// Each chat type in its own colour (the phone app's), icon and corner, so
// a customer's message never reads as a supplier's.
const AUDIENCE_STYLE = {
  customer: { color: "#00a884", Icon: UserOutlined, placement: "bottomLeft", label: "Customer" },
  supplier: { color: "#2f6bd8", Icon: ShopOutlined, placement: "topLeft", label: "Supplier" },
};

// The message as it looks in its thread: an incoming bubble on the chat's
// wallpaper colour, in the customer's or supplier's light or dark theme.
// The time sits in the bubble's footer, as in the chat thread.
function MessageBubble({ text, audience, mediaKind, sentAt }) {
  // Read the current appearance in the popup itself so an already-open notice
  // also follows the theme switch, without replaying its notification event.
  const { isDark } = useAppearance();
  const theme = ct(isDark, audience);
  const media = MEDIA_MESSAGE_LABELS[mediaKind];
  const timestamp = sentAt ? dayjs(sentAt) : null;
  return <div data-testid="chat-notification-bubble" data-audience={audience} style={{ position: "relative", overflow: "hidden", marginTop: 6, padding: 8, borderRadius: 8, background: theme.bg }}>
    <div aria-hidden="true" data-testid="chat-notification-wallpaper" style={{ position: "absolute", inset: 0, backgroundImage: `url(${chatBackground})`, backgroundRepeat: "repeat", backgroundPosition: "top left", filter: theme.bgFilter, opacity: theme.bgOpacity, pointerEvents: "none" }} />
    <div data-testid="chat-notification-message" style={{ position: "relative", display: "flex", flexDirection: "column", width: "100%", maxWidth: "100%", boxSizing: "border-box",
      padding: "6px 9px 4px", borderRadius: "0 8px 8px 8px", background: theme.bubbleIn, color: theme.text, boxShadow: "0 1px 0.5px rgba(11,20,26,0.13)" }}>
      <div style={{ display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 4, overflow: "hidden",
        fontSize: 13, lineHeight: "19px", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{media && <><span role="img" aria-label={media[1]}>{media[0]}</span>{" "}</>}<span dangerouslySetInnerHTML={{ __html: formatWaTextInlineHtml(text || media?.[1], isDark) }} /></div>
      {timestamp?.isValid() && <div data-testid="chat-notification-time" style={{ alignSelf: "flex-end", marginTop: 1, fontSize: 10, color: theme.textSub, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
        <span title={timestamp.format("DD MMM YYYY, hh:mm A")}>{timestamp.format("hh:mm A")}</span>
      </div>}
    </div>
  </div>;
}

export function CanonicalChatNotifications() {
  const messaging = useCanonicalMessaging();
  const settingsActor = personalSettingsUserId(messaging.currentAgentId);
  const preferences = useChatNotificationPreferences(settingsActor);
  const navigate = useNavigate();
  const [rawApi, holder] = useSessionNotification();
  const api = useMemo(() => withNotificationActions(rawApi), [rawApi]);
  const seen = useRef(new Set());
  useEffect(() => {
    seen.current.clear();
    api.destroy();
    return () => api.destroy();
  }, [settingsActor, api]);
  useEffect(() => {
    if (settingsActor == null) return;
    const events = messaging.recentMessageEvents || [];
    const recentIds = new Set(events.map(event => event.id));
    seen.current.forEach(id => { if (!recentIds.has(id)) seen.current.delete(id); });
    for (const event of events) {
    if (!event.id || seen.current.has(event.id)) continue;
    const message = messaging.messages[event.message_id];
    if (!message) continue;
    if (message.direction !== "inbound" || message.kind === "system") { seen.current.add(event.id); continue; }
    const conversation = messaging.conversations[message.conversation_id];
    const account = messaging.channelAccounts.find(item => item.id === (conversation?.channel_account_id || message.channel_account_id));
    if (!account) continue;
    seen.current.add(event.id);
    if (messaging.activeConversationIdRef.current === message.conversation_id && document.hasFocus() && document.visibilityState === "visible") continue;
    // Popup and sound are each switched per audience in User Settings.
    const supplier = account.audience === "supplier";
    if (supplier ? preferences.supplierSound : preferences.customerSound) {
      playNotificationSound(incomingSound, () => {
        const latest = loadChatNotificationPreferences(settingsActor);
        return supplier ? latest.supplierSound : latest.customerSound;
      });
    }
    if (!(supplier ? preferences.supplier : preferences.customer)) continue;
    const key = `chat-${message.conversation_id}`;
    const audience = supplier ? "supplier" : "customer";
    const look = AUDIENCE_STYLE[audience];
    // Canonical audio bubbles are voice messages; internal chat separately
    // distinguishes music/audio uploads from recorded voice notes.
    const mediaKind = message.kind === "audio" ? "voice" : message.kind;
    const media = MEDIA_MESSAGE_LABELS[mediaKind];
    const preview = media
      ? message.content?.caption || (message.kind === "document" ? message.content?.attachment?.file_name : null) || media[1]
      : message.content?.text || conversation?.last_message?.text || "New attachment";
    api.info({
      duration: 10,
      key, message: <ChatNotificationTitle conversationId={message.conversation_id} senderName={message.sender?.display_name} />,
      icon: supplier ? <span aria-label={`${look.label} message`} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 26, height: 26, borderRadius: "50%", background: look.color, color: "#fff", fontSize: 14 }}><look.Icon /></span> : <CustomerNotificationAvatar conversationId={message.conversation_id} />,
      style: { borderLeft: `4px solid ${look.color}` },
      description: <MessageBubble text={preview} mediaKind={mediaKind} sentAt={message.created_at || message.occurred_at} audience={supplier ? "supplier" : "customer"} />,
      placement: look.placement,
      actions: <Button size="small" icon={<WhatsAppOutlined />} style={{ color: look.color, borderColor: look.color }} onClick={() => {
        api.destroy(key);
        navigate("/querysetv4", { state: { openChatId: message.conversation_id, openMessageId: message.id } });
      }}>Open chat</Button>,
    });
    }
  }, [messaging, api, navigate, preferences, settingsActor]);
  return <AppTheme>{holder}</AppTheme>;
}
